import { CategoryGroup } from "@/features/MenuPage/types";
import { Maybe, MenuEntity, ProductEntity } from "@/generated";

export const groupMenuItemsByCategory = (
  menu: Maybe<MenuEntity> | undefined,
  products: ProductEntity[] | undefined
): CategoryGroup[] => {
  // If no products are provided, use web_catalog_items directly
  const itemsToProcess = products?.length
    ? products
    : menu?.attributes?.web_catalog_items?.data || [];

  // Initialize accumulator with categories and uncategorized array
  const grouped = itemsToProcess.reduce<{
    categorized: CategoryGroup[];
    uncategorized: any[];
  }>(
    (acc, item) => {
      const categoryId = item?.attributes?.category?.data?.id;
      const categoryName = item?.attributes?.category?.data?.attributes?.name;

      const productDetails: any = {
        id: item.id,
        title: item?.attributes?.title?.trim(), // Trim whitespace
        description: item?.attributes?.description?.trim(),
        price: item?.attributes?.price,
        image: item?.attributes?.media?.data?.[0]?.attributes?.url,
        category:
          categoryId && categoryName
            ? {
                id: categoryId,
                name: categoryName,
              }
            : undefined,
        weight: item?.attributes?.weight,
        calories: item?.attributes?.total_calories,
        nutritional_information: item?.attributes?.nutritional_information,
        ingredients: item?.attributes?.ingredients?.data?.map(
          (ingredient) => ingredient?.attributes?.name
        ) as string[],
        allergens: item?.attributes?.ingredients?.data?.flatMap((ingredient) =>
          ingredient?.attributes?.allergens?.data?.map(
            (allergen) => allergen?.attributes?.name
          )
        ) as string[],
      };

      if (categoryId && categoryName) {
        // Find existing category or create new one
        const existingCategory = acc.categorized.find(
          (category) => category.id === categoryId
        );

        if (existingCategory) {
          existingCategory.items.push(productDetails);
        } else {
          acc.categorized.push({
            id: categoryId,
            name: categoryName,
            items: [productDetails],
          });
        }
      } else {
        // Add to uncategorized array
        acc.uncategorized.push(productDetails);
      }

      return acc;
    },
    { categorized: [], uncategorized: [] }
  );

  // If there are uncategorized items, add them as a separate category
  if (grouped?.uncategorized && grouped?.uncategorized?.length > 0) {
    grouped?.categorized.push({
      id: "uncategorized",
      name: "Uncategorized",
      items: grouped?.uncategorized,
    });
  }

  return grouped?.categorized || [];
};
